import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ProposalData, ProposalList } from 'ts/shared/interfaces/ProposalData';
import Button from 'components/ui/Button/Button';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { showError, showSuccess } from 'utils/utils';
import useRecaptcha from 'hooks/useRecaptcha';
import { saveProposal as saveProposalToIdb } from 'utils/shared/idbUrl';

import {
  TripPreviewActiveState,
  TripPreviewSlotView,
  applyHotelDataDraftToProposalHotel,
  buildTripBuilderPlainProposalData,
  buildTripPreviewInputData,
  buildTripPreviewModel,
  buildTripPreviewProductDeleteReports,
  buildTripPreviewProductUpdateReports,
  clearFlightKeyFromActive,
  clearRoundTripFlightKeys,
  getHotelCancellationSettings,
  getTripBuilderPlainPreviewStorageKey,
  getTripPreviewHotelByCardKey,
  isHotelDataConfigOnlyPayload,
  parseTripPreviewDisplayTotalDeltaFromInputData,
  parseTripPreviewHotelImagesFromInputData,
  parseTripPreviewProposalImageFromInputData,
  parseTripPreviewRequirementByRow,
  resolveLanguageFromProposal,
  resolveTripPreviewActiveFromInputData,
  shouldLoadGoogleForProposal,
  syncRoundTripFlight,
  TripBuilderHotelDataDraftPayload,
  TripBuilderHotelDataState,
  TripBuilderHotelConfigDraftState,
  getTripPreviewProductByCardKey,
  buildTripPreviewEditedProductReports,
  createProposalProductSnapshot,
  withCurrencyChangedProductsMarked,
  updateProposalCityForSlot,
} from 'utils/shared/tripResultBuilderPreview';
import {
  applyDraftPayloadToIdbHotel,
  applyIdbHotelConfigsToInputData,
  buildAllIdbAddReports,
  buildTripBuilderSlotDraftPlans,
  emptyTripBuilderIdbDrafts,
  findIdbHotelByCardKey,
  getIdbHotelExternalParam,
  getIdbFlightsToAdd,
  getIdbHotelsToAdd,
  getIdbTransfersToAdd,
  loadIdbProposalDrafts,
  mapHotProposalToModalHotel,
  removeIdbFlightByCardKey,
  removeIdbHotelByCardKey,
  removeIdbTransferByCardKey,
  TripBuilderIdbDrafts,
  updateIdbHotelByCardKey,
} from 'utils/shared/tripResultBuilderIdb';
import TripBuilderPreviewSlots from './TripBuilderPreviewSlots';
import { TripBuilderPreviewProvider } from 'contexts/TripBuilderPreviewContext';
import { Proposal, TripSlotRequirement } from 'ts/shared/interfaces/CreateProposalParams';
import md5 from 'md5';
import { enrichExistingProposalHotelsData, getProposalGallery, getProposalPreview } from 'services/proposal';
import { availableLanguages } from 'constants/constant';
import { updateProposalD } from 'services/cms';
import withErrorBoundary from '../../withErrorBoundary';
import { buildTripBuilderAdditionalInfoUpdateParams, getTripBuilderFirstServiceDateFromProposalData, mapProposalToTripBuilderAdditionalInfo, migrateRecordKey, migrateTransitionKeys, replaceSlotKeyCity, TripBuilderAdditionalInfoValues, tripRowKeys } from 'utils/shared/tripBuilder';
import TripBuilderAdditionalInfoModal from './TripBuilderAdditionalInfoModal';
import TripBuilderHeaderInfo from './TripBuilderHeaderInfo';
import ProposalActions from '../ProposalActions';
import { MainContext } from 'contexts/MainContext';

interface TripBuilderProposalProps {
  proposal: ProposalData;
  onProposalUpdated?: (proposal: ProposalData) => void;
  onProposalSaved?: (proposal: ProposalData) => void;
  isUneditable?: boolean;
  onEditProposal?: () => void;
}

const TripBuilderProposal: FC<TripBuilderProposalProps> = ({
  proposal,
  onProposalUpdated,
  onProposalSaved,
  ...props
}) => {
  const { t, i18n } = useTranslation();
  const { user, initSiteData } = useContext(MainContext);
  const { setProposal: setDraftProposal } = useContext(MainContext);
  const { setProposal } = useContext(MainContext);
  const { handleRecapcha } = useRecaptcha();
  const [language, setLanguage] = useState(resolveLanguageFromProposal(proposal.externalParam_1));
  const [active, setActive] = useState<TripPreviewActiveState>(() =>
    resolveTripPreviewActiveFromInputData(proposal),
  );
  const [hotelImageBySlot, setHotelImageBySlot] = useState<Record<string, string>>(() =>
    parseTripPreviewHotelImagesFromInputData(proposal),
  );
  const initialHotelImageBySlotRef = useRef(parseTripPreviewHotelImagesFromInputData(proposal));
  const [proposalImage, setProposalImage] = useState(() =>
    parseTripPreviewProposalImageFromInputData(proposal),
  );
  const [requirementByRow, setRequirementByRow] = useState<Record<string, TripSlotRequirement>>(() =>
    parseTripPreviewRequirementByRow(proposal),
  );
  const [displayTotalDelta, setDisplayTotalDelta] = useState(() =>
    parseTripPreviewDisplayTotalDeltaFromInputData(proposal),
  );
  const [showCancellationDate, setShowCancellationDate] = useState(() =>
    getHotelCancellationSettings(proposal).showCancellationDate,
  );
  const [cancellationDateDelta, setCancellationDateDelta] = useState(() =>
    getHotelCancellationSettings(proposal).cancellationDateDelta,
  );
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [excludedCardKeys, setExcludedCardKeys] = useState<Record<string, true>>({});
  const [hotelDataByCardKey, setHotelDataByCardKey] = useState<Record<string, TripBuilderHotelDataState>>({});
  const [hotelConfigByCardKey, setHotelConfigByCardKey] = useState<
    Record<string, TripBuilderHotelConfigDraftState>
  >({});
  const [idbDrafts, setIdbDrafts] = useState<TripBuilderIdbDrafts>(emptyTripBuilderIdbDrafts);
  const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState<TripBuilderAdditionalInfoValues>(() =>
    mapProposalToTripBuilderAdditionalInfo(proposal),
  );
  const canSave = user?.userCode === proposal.userU;
  const key = proposal.id + '_' + md5(proposal.id + '') + '_' + proposal.userC + '_' + proposal.companyId;
  const showIdbDrafts = !props.isUneditable;
  const initialProductsRef = useRef(createProposalProductSnapshot(proposal));

  const toggleExcludedCard = (cardKey: string) => {
    setExcludedCardKeys((prev) => {
      const next = { ...prev };
      if (next[cardKey]) {
        delete next[cardKey];
      } else {
        next[cardKey] = true;
      }
      return next;
    });
  };

  useEffect(() => {
    void getProposalGallery();
  }, []);

  useEffect(() => {
    const initialHotelImages = parseTripPreviewHotelImagesFromInputData(proposal);
    initialHotelImageBySlotRef.current = initialHotelImages;
    setActive(resolveTripPreviewActiveFromInputData(proposal));
    setHotelImageBySlot(initialHotelImages);
    setProposalImage(parseTripPreviewProposalImageFromInputData(proposal));
    setRequirementByRow(parseTripPreviewRequirementByRow(proposal));
    setDisplayTotalDelta(parseTripPreviewDisplayTotalDeltaFromInputData(proposal));
    setShowCancellationDate(getHotelCancellationSettings(proposal).showCancellationDate);
    setCancellationDateDelta(getHotelCancellationSettings(proposal).cancellationDateDelta);
    setLanguage(resolveLanguageFromProposal(proposal.externalParam_1));
    setAdditionalInfo(mapProposalToTripBuilderAdditionalInfo(proposal));
    setExcludedCardKeys({});
    setHotelDataByCardKey({});
    setHotelConfigByCardKey({});
  }, [proposal]);

  useEffect(() => {
    initialProductsRef.current = createProposalProductSnapshot(proposal);
  }, [proposal]);

  useEffect(() => {
    if (!showIdbDrafts) {
      setIdbDrafts(emptyTripBuilderIdbDrafts());
      return;
    }

    const loadDrafts = async () => {
      setIdbDrafts(await loadIdbProposalDrafts());
    };

    void loadDrafts();
    const onFocus = () => {
      void loadDrafts();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [showIdbDrafts]);

  const model = useMemo(
    () =>
      buildTripPreviewModel(proposal, active, {
        requirementByRow,
        displayTotalDelta,
        idbDrafts: showIdbDrafts ? idbDrafts : undefined,
      }),
    [proposal, active, requirementByRow, displayTotalDelta, idbDrafts, showIdbDrafts],
  );

  const fullName = useMemo(
    () => [additionalInfo.firstName, additionalInfo.lastName].filter(Boolean).join(' ').trim(),
    [additionalInfo.firstName, additionalInfo.lastName],
  );

  const firstServiceDate = useMemo(
    () => getTripBuilderFirstServiceDateFromProposalData(proposal),
    [proposal],
  );

  const slotDraftPlans = useMemo(
    () => buildTripBuilderSlotDraftPlans(model.slots, idbDrafts, showIdbDrafts),
    [model.slots, idbDrafts, showIdbDrafts],
  );

  const persistIdbDrafts = async (next: TripBuilderIdbDrafts) => {
    let nextProposal!: Proposal;

    flushSync(() => {
      setIdbDrafts(next);
      setDraftProposal((prev) => {
        nextProposal = {
          ...prev,
          hotels: next.hotels,
          flights: next.flights,
          transfers: next.transfers,
        };
        return nextProposal;
      });
    });

    await saveProposalToIdb('proposal', nextProposal);
  };

  const removeIdbHotel = async (cardKey: string) => {
    await persistIdbDrafts({
      ...idbDrafts,
      hotels: removeIdbHotelByCardKey(idbDrafts.hotels, cardKey),
    });
  };

  const removeIdbFlight = async (cardKey: string) => {
    await persistIdbDrafts({
      ...idbDrafts,
      flights: removeIdbFlightByCardKey(idbDrafts.flights, cardKey),
    });
  };

  const removeIdbTransfer = async (cardKey: string) => {
    await persistIdbDrafts({
      ...idbDrafts,
      transfers: removeIdbTransferByCardKey(idbDrafts.transfers, cardKey),
    });
  };

  const getDraftHotelForCard = (cardKey: string) => {
    const hotel = findIdbHotelByCardKey(idbDrafts.hotels, cardKey);
    return hotel ? mapHotProposalToModalHotel(hotel) : undefined;
  };

  const getDraftHotelExternalParamForCard = (cardKey: string) =>
    getIdbHotelExternalParam(findIdbHotelByCardKey(idbDrafts.hotels, cardKey), proposal.externalParam_1);

  const onSaveDraftHotelData = async (cardKey: string, payload: TripBuilderHotelDataDraftPayload) => {
    await persistIdbDrafts({
      ...idbDrafts,
      hotels: updateIdbHotelByCardKey(idbDrafts.hotels, cardKey, (hotel) =>
        applyDraftPayloadToIdbHotel(hotel, payload),
      ),
    });
  };

  const getHotelForCard = (cardKey: string) =>
    getTripPreviewHotelByCardKey(proposal, cardKey, hotelDataByCardKey);

  const getProductForCard = (cardKey: string) => getTripPreviewProductByCardKey(proposal, cardKey);

  const buildSaveInputData = (hotelsToAdd: ReturnType<typeof getIdbHotelsToAdd> = []) => {
    let nextInputData = buildTripPreviewInputData(proposal, active, {
      proposalImage,
      rows: requirementByRow,
      displayTotalDelta,
      hotelConfigByCardKey,
      language,
    });

    if (hotelsToAdd.length) {
      nextInputData = applyIdbHotelConfigsToInputData(nextInputData, hotelsToAdd);
    }

    return nextInputData;
  };

  const onSaveProductEdit = (updatedProduct: ProposalList) => {
    const nextProposal: ProposalData = {
      ...proposal,
      listOfProducts: (proposal.listOfProducts || []).map((product) =>
        product.id === updatedProduct.id ? { ...updatedProduct, edited: true } : product,
      ),
    };
    onProposalUpdated?.(JSON.parse(JSON.stringify(nextProposal)));
  };

  const onSaveCityName = (
    slot: { slotKey: string; city: string; checkIn: string; checkOut: string },
    newCity: string,
  ) => {
    const trimmedCity = newCity.trim();
    if (!trimmedCity) {
      return;
    }

    const { slotKey, checkIn, checkOut } = slot;
    const newSlotKey = replaceSlotKeyCity(slotKey, trimmedCity, checkIn, checkOut);
    const nextProposal = updateProposalCityForSlot(proposal, slotKey, trimmedCity);
    onProposalUpdated?.(JSON.parse(JSON.stringify(nextProposal)));

    if (newSlotKey !== slotKey) {
      setActive((prev) => ({
        ...prev,
        activeHotel: migrateRecordKey(prev.activeHotel, slotKey, newSlotKey),
        activeTransfer: migrateRecordKey(prev.activeTransfer, slotKey, newSlotKey),
        activeMiddle: migrateTransitionKeys(prev.activeMiddle, slotKey, newSlotKey),
      }));
      setHotelImageBySlot((prev) => migrateRecordKey(prev, slotKey, newSlotKey));
      setRequirementByRow((prev) => {
        const next = { ...prev };
        const oldHotelRowKey = tripRowKeys.hotel(slotKey);
        const newHotelRowKey = tripRowKeys.hotel(newSlotKey);
        const oldTransferRowKey = tripRowKeys.transfer(slotKey);
        const newTransferRowKey = tripRowKeys.transfer(newSlotKey);

        if (next[oldHotelRowKey] !== undefined) {
          next[newHotelRowKey] = next[oldHotelRowKey];
          delete next[oldHotelRowKey];
        }
        if (next[oldTransferRowKey] !== undefined) {
          next[newTransferRowKey] = next[oldTransferRowKey];
          delete next[oldTransferRowKey];
        }
        return next;
      });
    }
  };

  const saveProposal = async () => {
    setSaving(true);

    try {
      const proposalForReports = withCurrencyChangedProductsMarked(proposal, initialProductsRef.current);
      const hotelsToAdd = getIdbHotelsToAdd(idbDrafts.hotels, model.slots, proposal);
      const flightsToAdd = getIdbFlightsToAdd(idbDrafts.flights, model.slots, proposal);
      const transfersToAdd = getIdbTransfersToAdd(idbDrafts.transfers, model.slots, proposal);
      const inputData = buildSaveInputData(hotelsToAdd);
      const additionalInfoParams = buildTripBuilderAdditionalInfoUpdateParams(additionalInfo, proposal);
      const reportsList = [
        ...buildTripPreviewProductUpdateReports(proposalForReports, {
          hotelImageBySlot,
          initialHotelImageBySlot: initialHotelImageBySlotRef.current,
          requirementByRow,
          hotelCancellationSettings: {
            showCancellationDate,
            cancellationDateDelta: +cancellationDateDelta,
          },
          hotelDataByCardKey,
        }),
        ...buildTripPreviewProductDeleteReports(proposalForReports, Object.keys(excludedCardKeys)),
        ...buildAllIdbAddReports({
          hotelsToAdd,
          flightsToAdd,
          transfersToAdd,
          slots: model.slots,
          requirementByRow,
          hotelImageBySlot,
          showCancellationDate,
          cancellationDateDelta: +cancellationDateDelta,
          language,
        }),
        ...buildTripPreviewEditedProductReports(proposalForReports),
      ];

      if (!reportsList.length && !inputData) {
        return;
      }

      const response = await updateProposalD({
        previewKeyPublic: key,
        proposalId: proposal.id,
        comments: proposal.note,
        ...(inputData ? { inputData } : {}),
        ...(reportsList.length ? { reportsList } : {}),
        ...additionalInfoParams,
      });

      if (!response?.updated) {
        showError(t('something_went_wrong', { defaultValue: 'Something went wrong' }));
        return;
      }

      const freshProposal = await getProposalPreview(key);
      if (freshProposal) {
        initialProductsRef.current = createProposalProductSnapshot(freshProposal);
        onProposalUpdated?.(JSON.parse(JSON.stringify(freshProposal)));
      }

      const savedProposal = freshProposal || proposal;
      const emptyDraft = { hotels: [], flights: [], dynamic: [], ilPackages: [], transfers: [] };

      await saveProposalToIdb('proposal', emptyDraft);
      setDraftProposal((prev) => ({
        ...emptyDraft,
        existingProposal: savedProposal,
      }));
      setIdbDrafts(emptyTripBuilderIdbDrafts());

      showSuccess(t('proposal.updated', { defaultValue: 'Proposal updated' }));
      onProposalSaved?.(savedProposal);
    } catch {
      showError(t('something_went_wrong', { defaultValue: 'Something went wrong' }));
    } finally {
      setSaving(false);
    }
  };

  const onSaveHotelDataDraft = (cardKey: string, payload: TripBuilderHotelDataDraftPayload) => {
    const hotel = getTripPreviewHotelByCardKey(proposal, cardKey);
    if (!hotel) {
      return;
    }

    if (isHotelDataConfigOnlyPayload(payload)) {
      setHotelConfigByCardKey((prev) => ({
        ...prev,
        [cardKey]: {
          ...prev[cardKey],
          ...(payload.hotelInfoConfig ? { hotelInfoConfig: payload.hotelInfoConfig } : {}),
          ...(payload.claudeHotelInfoConfig
            ? { claudeHotelInfoConfig: payload.claudeHotelInfoConfig }
            : {}),
        },
      }));
      return;
    }

    const { serpHotel, tripAdvisorHotel } = applyHotelDataDraftToProposalHotel(hotel, payload);
    setHotelDataByCardKey((prev) => ({
      ...prev,
      [cardKey]: {
        serpHotel,
        tripAdvisorHotel,
        serpHotelEntire: payload.sourceSerpHotel,
        tripAdvisorHotelEntire: payload.sourceTripadvisorHotel,
      },
    }));
  };

  const setRowRequirement = (rowKey: string, value: TripSlotRequirement) => {
    setRequirementByRow((prev) => ({ ...prev, [rowKey]: value }));
  };

  const onClickPlainProposalPreview = () => {
    const plainProposal = buildTripBuilderPlainProposalData(proposal, active, excludedCardKeys, {
      language,
      proposalImage,
      displayTotalDelta,
    });
    sessionStorage.setItem(
      getTripBuilderPlainPreviewStorageKey(key),
      JSON.stringify(plainProposal),
    );
    window.open(
      window.location.hostname.includes('localhost')
        ? `/proposal/preview?id2=${encodeURIComponent(key)}&plainPreview=true`
        : `https://proposal.beprotravel.com/proposal/?id2=${encodeURIComponent(key)}&plainPreview=true`,
      '_blank',
    );
  };

  const onClickEditProposal = async () => {
    setLoadingEdit(true);
    try {
      const recaptchaToken = shouldLoadGoogleForProposal(proposal)
        ? await handleRecapcha()
        : undefined;

      const existingProposal = await enrichExistingProposalHotelsData(
        proposal,
        i18n.language,
        recaptchaToken,
        (updatedProposal) =>
          setProposal((prev: any) => ({ ...prev, existingProposal: updatedProposal })),
      );

      setProposal((prev: any) => ({
        ...prev,
        existingProposal,
      }));
      props.onEditProposal?.();
    } catch {
      showError(t('something_went_wrong', { defaultValue: 'Something went wrong' }));
    } finally {
      setLoadingEdit(false);
    }
  };

  const selectBeforeFlight = (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => {
    setActive((prev) => {
      if (prev.activeBefore[slot.rootSlotKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      const next = isRoundTrip ? prev : clearRoundTripFlightKeys(prev, slot.rootSlotKey, slot);
      if (isRoundTrip) {
        return syncRoundTripFlight(next, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...next,
        activeBefore: { ...next.activeBefore, [slot.rootSlotKey]: flightKey },
      };
    });
  };

  const selectAfterFlight = (slot: TripPreviewSlotView, flightKey: string, isRoundTrip: boolean) => {
    setActive((prev) => {
      if (prev.activeAfter[slot.rootSlotKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      const next = isRoundTrip ? prev : clearRoundTripFlightKeys(prev, slot.rootSlotKey, slot);
      if (isRoundTrip) {
        return syncRoundTripFlight(next, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...next,
        activeAfter: { ...next.activeAfter, [slot.rootSlotKey]: flightKey },
      };
    });
  };

  const selectMiddleFlight = (
    slot: TripPreviewSlotView,
    transitionKey: string,
    flightKey: string,
    isRoundTrip: boolean,
  ) => {
    setActive((prev) => {
      if (prev.activeMiddle[transitionKey] === flightKey) {
        return clearFlightKeyFromActive(prev, slot, flightKey);
      }
      if (isRoundTrip) {
        return syncRoundTripFlight(prev, slot, slot.rootSlotKey, flightKey);
      }
      return {
        ...prev,
        activeMiddle: { ...prev.activeMiddle, [transitionKey]: flightKey },
      };
    });
  };

  const selectHotel = (slotKey: string, hotelKey: string) => {
    setActive((prev) => {
      if (prev.activeHotel[slotKey] === hotelKey) {
        const next = { ...prev.activeHotel };
        delete next[slotKey];
        return { ...prev, activeHotel: next };
      }
      return {
        ...prev,
        activeHotel: { ...prev.activeHotel, [slotKey]: hotelKey },
      };
    });
  };

  const selectTransfer = (slotKey: string, transferKey: string) => {
    setActive((prev) => {
      if (prev.activeTransfer[slotKey] === transferKey) {
        const next = { ...prev.activeTransfer };
        delete next[slotKey];
        return { ...prev, activeTransfer: next };
      }
      return {
        ...prev,
        activeTransfer: { ...prev.activeTransfer, [slotKey]: transferKey },
      };
    });
  };

  return (
    <>
    <div className={`pr-tb-main pr-tb--edit${canSave ? ' pr-tb--share' : ''}`}>
        <div className="pr-tb-head">
          <TripBuilderHeaderInfo
            proposalId={proposal.id}
            clientName={fullName}
            firstServiceDate={firstServiceDate}
            expireDate={additionalInfo.expireDate}
            onEditAdditionalInfo={
              canSave && !props.isUneditable
                ? () => setShowAdditionalInfoModal(true)
                : undefined
            }
          />
        </div>

        {!model.slots.length && <div className="pr-tb-empty text-xsm text-center">No hotels in proposal</div>}

        {!!model.slots.length && (
          <TripBuilderPreviewProvider
            value={{
              canSave,
              excludedCardKeys,
              requirementByRow,
              hotelImageBySlot,
              externalParam_1: proposal.externalParam_1,
              toggleExcludedCard,
              setRowRequirement,
              setHotelImageForSlot: (slotKey, url) => {
                setHotelImageBySlot((prev) => {
                  const next = { ...prev };
                  if (url) {
                    next[slotKey] = url;
                  } else {
                    delete next[slotKey];
                  }
                  return next;
                });
              },
              selectBeforeFlight,
              selectAfterFlight,
              selectMiddleFlight,
              selectHotel,
              selectTransfer,
              removeIdbHotel,
              removeIdbFlight,
              removeIdbTransfer,
              getHotelForCard,
              getProductForCard,
              getDraftHotelForCard,
              onSaveProductEdit,
              onSaveHotelDataDraft,
              onSaveDraftHotelData,
              getDraftHotelExternalParam: getDraftHotelExternalParamForCard,
              onSaveCityName,
            }}
          >
            <TripBuilderPreviewSlots plans={slotDraftPlans} />
          </TripBuilderPreviewProvider>
        )}

        {canSave && (
          <div className="pr-tb-footer pr-tb-footer--stack flex-column">
            <div className="pr-tb-footer-row flex-v-center flex-wrap w100p">
              <ProposalActions proposal={proposal} shareLinkOnly />
            </div>
            <div className="pr-tb-footer-row flex-v-center flex-wrap w100p">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!props.isUneditable && (
                  <div style={{ width: 180 }}>
                    <SelectCustom
                      options={availableLanguages
                        .map((lang) => ({
                          label: lang.name,
                          value: lang.lang,
                          flag: lang.country,
                        }))
                        .filter(
                          (lang) =>
                            !(
                              ['IT', 'ES', 'GR'].includes(initSiteData?.other?.defaultNationality || '') &&
                              lang.value === 'he'
                            ),
                        )}
                      value={language}
                      onChange={setLanguage}
                      menuPlacement="top"
                    />
                  </div>
                )}
                {!props.isUneditable && (
                  <Button
                    className="btn btn-default"
                    title="Additional info"
                    onClick={() => setShowAdditionalInfoModal(true)}
                  />
                )}
                <Button
                  className="btn btn-default"
                  title="Proposal View"
                  onClick={onClickPlainProposalPreview}
                />
                <Button
                  className="btn btn-default"
                  title="Trip Builder View"
                  onClick={() => {
                    window.open(
                      `https://proposal.beprotravel.com/trip-builder/?id2=${encodeURIComponent(key)}&tripBuilder=true`,
                      '_blank',
                    );
                  }}
                />
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                {props.isUneditable && (
                  <Button
                    className="btn btn-primary"
                    title="Edit proposal"
                    disabled={loadingEdit}
                    onClick={onClickEditProposal}
                  />
                )}
                {!props.isUneditable && (
                  <Button
                    className="btn btn-primary"
                    title="Save Trip Builder"
                    disabled={saving || !model.slots.length}
                    onClick={() => void saveProposal()}
                  />
                )}
              </div>
            </div>
          </div>
        )}
    </div>
      {showAdditionalInfoModal && (
        <TripBuilderAdditionalInfoModal
          values={additionalInfo}
          close={() => setShowAdditionalInfoModal(false)}
          onSave={setAdditionalInfo}
          proposalSettings={{
            showCancellationDate,
            displayTotalDelta,
            cancellationDateDelta,
            proposalImage,
          }}
          onProposalSettingsSave={(settings) => {
            setShowCancellationDate(settings.showCancellationDate);
            setDisplayTotalDelta(settings.displayTotalDelta);
            setCancellationDateDelta(settings.cancellationDateDelta);
            setProposalImage(settings.proposalImage);
          }}
        />
      )}
    </>
  );
};

export default withErrorBoundary(TripBuilderProposal, 'TripBuilderProposal');
